import { useCallback, useMemo, useState } from "react";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  useEntityList,
  type DataTableColumn,
} from "@pericles/framework-frontend";

import { CUSTOMER_LABEL, NAV_LABELS } from "../config/brand";

import { EntityDeleteButton } from "./EntityDeleteButton";
import { PageShell } from "./ui";

interface CustomerListItem {
  id: string | number;
  name?: string;
  code?: string;
  sector?: string;
  countryCode?: string;
  contextGenerationStatus?: string;
  contextGenerationError?: string | null;
  contextGeneratedAt?: string;
  createdAt?: string;
}

interface CustomersListScreenProps {
  entityName: string;
  activeCustomerId: string;
  onActiveCustomerChange: (customerId: string) => void;
  pageSize?: number;
  onRowClick?: (row: CustomerListItem) => void;
}

export function CustomersListScreen({
  entityName,
  activeCustomerId,
  onActiveCustomerChange,
  pageSize = 20,
  onRowClick,
}: CustomersListScreenProps): JSX.Element {
  const [page, setPage] = useState(0);
  const { data, total, isLoading, isError, error, refetch } = useEntityList<CustomerListItem>({
    entity: entityName,
    page,
    pageSize,
  });

  const pluralLabel = NAV_LABELS.customers;
  const lowerPluralLabel = pluralLabel.toLowerCase();
  const lowerSingularLabel = CUSTOMER_LABEL.toLowerCase();

  const displayTotal = typeof total === "number" ? total : data?.length ?? 0;

  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<DataTableColumn<CustomerListItem>[]>(
    () => [
      { id: "name", header: "Name", accessor: (row) => row.name ?? "" },
      { id: "code", header: "Code", accessor: (row) => row.code ?? "" },
      { id: "sector", header: "Sector", accessor: (row) => row.sector ?? "" },
      { id: "countryCode", header: "Country", accessor: (row) => row.countryCode ?? "" },
      {
        id: "contextGenerationStatus",
        header: "Context status",
        accessor: (row) => row.contextGenerationStatus ?? "idle",
      },
      { id: "createdAt", header: "Created", accessor: (row) => row.createdAt ?? "" },
      {
        id: "actions",
        header: "Actions",
        accessor: (row) => (
          <EntityDeleteButton
            entityName={entityName}
            entityId={row.id}
            confirmMessage={`Delete this ${lowerSingularLabel}? This action cannot be undone.`}
            onDeleted={() => {
              refetch();
              if (String(row.id) === activeCustomerId) {
                onActiveCustomerChange("all");
              }
            }}
          />
        ),
        width: 140,
      },
    ],
    [activeCustomerId, entityName, lowerSingularLabel, onActiveCustomerChange, refetch],
  );

  const isActiveRow = useCallback(
    (row: CustomerListItem) => (activeCustomerId === "all" ? false : String(row.id) === activeCustomerId),
    [activeCustomerId],
  );

  const handleRowClick = useCallback(
    (row: CustomerListItem) => {
      const id = row.id ? String(row.id) : null;
      if (id) {
        onActiveCustomerChange(id);
      }
      onRowClick?.(row);
    },
    [onActiveCustomerChange, onRowClick],
  );

  const errorNode = isError ? (
    <span className="text-sm text-red-600">
      {error instanceof Error ? error.message : `Failed to load ${lowerPluralLabel}`}
    </span>
  ) : undefined;

  return (
    <PageShell>
      <PageHeader title={pluralLabel} />

      <Card>
        <DataTable<CustomerListItem>
          columns={columns}
          data={rows}
          isLoading={isLoading}
          error={errorNode}
          page={page}
          pageSize={pageSize}
          totalCount={displayTotal}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          rowClassName={(row) => (isActiveRow(row) ? "data-table__row--active" : undefined)}
          emptyState={
            <EmptyState title={`No ${lowerPluralLabel}`} description={`Start by creating a ${lowerSingularLabel}.`} />
          }
        />
      </Card>
    </PageShell>
  );
}
